<template>
  <!--
    Gemeinsame Vorlage für alle 6 Marketing-Seiten, aufgebaut auf AppPage (Jira-Stil):
      Breadcrumb Marketing / Marke / Seite · Icon + Titel (+ #lozenges) · Untertitel ·
      Kontextzeile nur mit Seiten-Extras (#context, z. B. Mediaplan-Status) ·
      Toolbar (Filterleiste aus dem Layout) · #kpis · #main · Details (default) ·
      Accordions "Kennzahlen & Definitionen" und #legend.
    Kanalfilter kommen über #channel-filters in die 2. Filterebene der Toolbar.
  -->
  <PageFilters v-if="$slots['channel-filters']">
    <slot name="channel-filters" />
  </PageFilters>
  <AppPage
    :crumbs="crumbs"
    :title="title"
    :icon="page.icon"
    :icon-color="page.color"
    :subtitle="subtitle"
    :loading="loading"
    :loaded-once="loadedOnce"
    :error="error"
    :loading-text="loadingText"
    @retry="$emit('retry')"
  >
    <template #lozenges><slot name="lozenges" /></template>
    <!-- Projekt, Kampagne und Zeitraum stehen schon in der Filterleiste – hier nur Seiten-Extras. -->
    <template v-if="$slots.context" #context>
      <slot name="context" />
    </template>

    <section v-if="$slots.kpis" class="mb-zone" aria-label="Kennzahlen">
      <slot name="kpis" />
    </section>
    <slot name="main" />
    <slot />
    <div v-if="legend.length || $slots.legend" class="mb-zone mb-zone--accordions">
      <MbAccordion v-if="legend.length" title="Kennzahlen & Definitionen" :count="legend.length">
        <KpiLegend :legend="legend" />
      </MbAccordion>
      <MbAccordion v-if="$slots.legend" :title="legendTitle">
        <slot name="legend" />
      </MbAccordion>
    </div>
  </AppPage>
</template>

<script setup>
import { computed, inject, provide, ref } from 'vue';
import { useRoute } from 'vue-router';
import AppPage from '@/components/ui/AppPage.vue';
import { CHANNEL_COLORS } from '../composables/designTokens';
import { useMarketingFilters } from '../composables/useMarketingFilters';
import PageFilters from './PageFilters.vue';
import MbAccordion from './MbAccordion.vue';
import KpiLegend from './KpiLegend.vue';

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  loadedOnce: { type: Boolean, default: false },
  error: { type: String, default: '' },
  loadingText: { type: String, default: 'Lade Daten…' },
  legend: { type: Array, default: () => [] },
  legendTitle: { type: String, default: 'Legende Schaltungstypen' }
});

defineEmits(['retry']);

// Projekt-Icon je Seite, Farbe = Kanalfarbe.
const PAGES = {
  'marketing-overview': { icon: 'fa-table-cells-large', color: '#23799D' },
  'marketing-digital': { icon: 'fa-display', color: CHANNEL_COLORS.digital },
  'marketing-social': { icon: 'fa-share-nodes', color: CHANNEL_COLORS.social },
  'marketing-email': { icon: 'fa-envelope', color: CHANNEL_COLORS.email },
  'marketing-traffic': { icon: 'fa-arrow-trend-up', color: CHANNEL_COLORS.traffic },
  'marketing-print': { icon: 'fa-newspaper', color: CHANNEL_COLORS.print }
};

const route = useRoute();
const page = computed(() => PAGES[route.name] || PAGES['marketing-overview']);

const { filters, queryFor } = useMarketingFilters();
const filterOptions = inject('mbFilterOptions', ref({}));

function optionLabel(items, id, fallback) {
  if (!id) return fallback;
  const match = (items || []).find((item) => String(item.id) === String(id));
  return match?.name || fallback;
}

const brandLabel = computed(() => optionLabel(filterOptions.value.products, filters.product_id, 'Alle Marken'));

const crumbs = computed(() => [
  { label: 'Marketing', to: { name: 'marketing-overview', query: queryFor('marketing-overview') } },
  { label: brandLabel.value },
  ...(route.name !== 'marketing-overview' ? [{ label: props.title }] : [])
]);

// Definitionen als Tooltip an jeder Kennzahl-Karte – KpiTile liest sie über inject.
const definitions = computed(() => Object.fromEntries(
  (props.legend || []).map((item) => [item.kpi_key, item])
));
provide('mbKpiDefinitions', definitions);
</script>
